import { NextPage } from 'next';
import { apiPlansGetV2, planPageType } from 'api/plans/get';
import { getYotpoBottomline } from 'lib/utils/get-yotpo-bottomline';
import { getYotpoLlmSchema } from 'lib/utils/get-yotpo-llm-schema';

import { getProductProps } from 'lib/utils/get-product-static-props';
import { attachPdpModulesToProps } from 'lib/pdp-modules/get-pdp-modules';
import type { PdpModules, PdpModulesSource } from 'lib/pdp-modules/types';
import { DM02_FOOTER_DISCLAIMER } from 'components/pdp/cobiotics-footer-disclaimer';
import { getShopifyVariantResults } from 'lib/shopify/get-shopify-variants-direct';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import { mqXXLargeMin, mqLargeMin } from 'styled/tools/media-queries';
import { createGlobalStyle } from 'styled-components';
import { PageTypePdp } from 'components/styles/pdp';
import { YotpoReviewScript } from 'components/pdp/yotpo-reviews-script';

import { InternationalCurrencyModal } from 'components/pdp/international-currency-modal';
import Buybox from 'components/pdp/buybox';
import ProductEssentials from 'components/pdp/product-essentials';
import CobioticsMicrobiomeModule from 'components/pdp/cobiotics-microbiome/cobiotics-microbiome-module';
import { DM02_MICROBIOME } from 'components/pdp/cobiotics-microbiome/data';
import {
  ExperimentFlag,
  useFeatureFlags,
} from 'context-providers/feature-flag-provider';
import ProductBenefits from 'components/pdp/product-benefits';
import ProductNutrients from 'components/pdp/product-nutrients';
import ProductCompareChart from 'components/pdp/product-compare';
import ProductTesting from 'components/pdp/product-testing';
import ProductBundle from 'components/pdp/product-bundle';
import ProductHead from './components/ProductHead';
import { ReviewsDisplay } from 'components/pdp/reviews-display';
import { YotpoLlmSchema } from 'components/pdp/yotpo-llm-schema';
import StickyBar from 'components/global/StickyBar';
import { usePdpStickyBar } from 'hooks/use-pdp-sticky-bar';
import { Product } from 'entities/product';
import { usePdpProductViewed } from 'hooks/use-pdp-product-viewed';
import { PRODUCT_IMAGE_DM02 } from 'lib/product-data';

const GlobalStyleDailySynbiotic = createGlobalStyle`
  .site-content {
    background-color: var(--fill-primary);
  
    @media ${mqLargeMin}{
      padding: 0;
    }
    #srp0-sticky {
      z-index: 100;
      transform: translateY(0);
    }
    #add-to-cart-new {
      display: none;
    }
  }
  .optly-hero-text {
    top: 20% !important;
    @media ${mqXXLargeMin} {
      top: 25% !important;
    }
  }

  #add-to-cart-cta:focus {
    background-color: var(--fill-brand) !important;
  }

  ${PageTypePdp}
`;

interface DailyMultivitaminaPageProps {
  yotpoAverageScore: string | null;
  yotpoTotalReviews: string | null;
  yotpoLlmSchemaHtml?: string;
  products: Product[];
  siteBuy: any;
  productInfo: any;
  modules: PdpModules;
  modulesSource: PdpModulesSource;
}

const DailyMultivitaminPage: NextPage<DailyMultivitaminaPageProps> = ({
  yotpoAverageScore,
  yotpoTotalReviews,
  yotpoLlmSchemaHtml,
  siteBuy,
  productInfo,
  modules,
  products,
}) => {
  const { selectedSku, product, setSelectedSku } = usePdpProductViewed({
    products,
    sku: PRODUCT_SKUS.DAILY_MULTIVITAMIN,
  });
  const stickyBarProps = usePdpStickyBar(selectedSku, siteBuy);
  const { experimentFlags } = useFeatureFlags();
  const showMicrobiomeModule =
    experimentFlags[ExperimentFlag.CobioticsMicrobiomeModule];

  const productImages = [];
  productInfo?.media?.nodes?.forEach((mediaItem) => {
    if (mediaItem.mediaContentType === 'IMAGE') {
      productImages.push(mediaItem.previewImage.url);
    }
  });

  return (
    <>
      <GlobalStyleDailySynbiotic />
      <YotpoReviewScript />
      <ProductHead
        productName={productInfo.name}
        productImages={productImages}
        productDescription={productInfo.description}
        productSku={PRODUCT_SKUS.DAILY_MULTIVITAMIN}
        productGtin='850058995168'
        canonicalUrl='https://seed.com/daily-multivitamin'
        productPrice={productInfo?.priceRange?.minVariantPrice?.amount}
        pageTitle='Seed • DM-02® Daily Multivitamin | 20 Essential Vitamins + Minerals'
        ogImageUrl='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1759517366/structured-data/og-dm.png'
        ogImageAlt='DM-02® Daily Multivitamin | Best Price Available'
        yotpoAverageScore={yotpoAverageScore}
        yotpoTotalReviews={yotpoTotalReviews}
      />
      <div className='container'>
        <InternationalCurrencyModal location='Daily Synbiotic PDP' />
        {product && productInfo && (
          <>
            <Buybox
              siteBuy={siteBuy}
              useCartContext
              selectedSku={selectedSku}
              setSelectedSku={setSelectedSku}
              productInfo={productInfo}
              yotpoAverageScore={yotpoAverageScore}
              yotpoTotalReviews={yotpoTotalReviews}
              buyBox={modules.buyBox}
              formulatedTo='Formulated to°:'
              isCobiotics
            />
            {modules.essentialsModule && (
              <ProductEssentials
                productInfo={productInfo}
                essentials={modules.essentialsModule}
                microbiomeModule={
                  showMicrobiomeModule && (
                    <CobioticsMicrobiomeModule content={DM02_MICROBIOME} />
                  )
                }
              />
            )}
            {modules.benefitsModule && (
              <ProductBenefits benefits={modules.benefitsModule} />
            )}
            {modules.nutrientsModule && (
              <ProductNutrients
                nutrients={modules.nutrientsModule}
                productInfo={productInfo}
              />
            )}
            {modules.compareChartModule && (
              <ProductCompareChart
                compare={modules.compareChartModule}
                productInfo={productInfo}
              />
            )}
            {modules.testingStandardsModule && (
              <ProductTesting
                testingModule={modules.testingStandardsModule}
                productInfo={productInfo}
              />
            )}
            {modules.bundleModule && (
              <ProductBundle
                productInfo={productInfo}
                bundleModule={modules.bundleModule}
                product={product}
              />
            )}

            <StickyBar
              {...stickyBarProps}
              imageSrc={PRODUCT_IMAGE_DM02}
              buttonEventName='DM start now'
            />

            <ReviewsDisplay
              imageUrl='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1759517366/structured-data/og-dm.png'
              product={product}
              url='/daily-multivitamin'
            />
            <YotpoLlmSchema html={yotpoLlmSchemaHtml} />
          </>
        )}
      </div>
    </>
  );
};

export async function getStaticProps() {
  const products = await apiPlansGetV2(planPageType.all);
  const productId = PRODUCT_SKUS.DAILY_MULTIVITAMIN;
  const [{ yotpoAverageScore, yotpoTotalReviews }, yotpoLlmSchemaHtml] =
    await Promise.all([
      getYotpoBottomline(productId),
      getYotpoLlmSchema(productId),
    ]);

  const props = {
    products,
    yotpoAverageScore,
    yotpoTotalReviews,
    yotpoLlmSchemaHtml,
  };

  const { results } = await getShopifyVariantResults({
    requiredSkus: [productId],
  });
  props.shopifyProducts = results;

  await getProductProps({
    props: props,
    productSku: productId,
    shopifyVariantData: props.shopifyProducts,
    footerDisclaimer: DM02_FOOTER_DISCLAIMER,
  });

  await attachPdpModulesToProps('dm-02', props);

  return {
    props,
    revalidate: 60,
  };
}

export default DailyMultivitaminPage;
